import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';

/**
 * MisconceptionAlert Component - 2025 Design System
 *
 * Displays detected misconceptions with:
 * - ROI calculation (time saved vs time invested)
 * - Automated lesson plan suggestions
 * - Evidence excerpts from student conversations
 * - Priority-based visual hierarchy
 *
 * Core feature for reducing teacher re-teaching time
 */
const MisconceptionAlert = ({ courseId, teacherId }) => {
  const [misconceptions, setMisconceptions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [addressingId, setAddressingId] = useState(null);

  useEffect(() => {
    fetchMisconceptions();
  }, [courseId]);

  const fetchMisconceptions = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        addressed: 'false' // Only show unaddressed misconceptions
      });

      if (courseId) {
        params.append('course_id', courseId);
      }

      const response = await fetch(
        `/api/moderation/teacher/misconceptions?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch misconceptions');
      }

      const data = await response.json();
      setMisconceptions(data.misconceptions || []);
      setSummary(data.summary || null);
    } catch (err) {
      console.error('Error fetching misconceptions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsAddressed = async (misconceptionId) => {
    setAddressingId(misconceptionId);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/moderation/teacher/misconceptions/${misconceptionId}/mark-addressed`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            addressed_at: new Date().toISOString(),
            approach_used: 'whole_class_demo',
            notes: 'Addressed with whole class'
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to mark as addressed');
      }

      // Remove from list
      setMisconceptions(prev => prev.filter(m => m.id !== misconceptionId));

      // Show success toast
      // TODO: Add toast notification system

    } catch (err) {
      console.error('Error marking as addressed:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setAddressingId(null);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-600 text-white border-red-700';
      case 'high':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'medium':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'low':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getPriorityIndicator = (priority) => {
    const colors = {
      critical: 'bg-red-600',
      high: 'bg-red-500',
      medium: 'bg-orange-400',
      low: 'bg-gray-300',
      default: 'bg-gray-300'
    };
    return (
      <div className={`w-3 h-3 rounded-full ${colors[priority] || colors.default}`} />
    );
  };

  const getROIColor = (roi) => {
    if (roi >= 3) return 'text-green-600';
    if (roi >= 2) return 'text-green-500';
    if (roi >= 1) return 'text-blue-600';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Misconceptions Detected</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading misconceptions...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full border-red-200">
        <CardHeader>
          <CardTitle className="text-red-700">Error Loading Misconceptions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600">{error}</p>
          <Button
            onClick={fetchMisconceptions}
            variant="danger"
            className="mt-4"
          >
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="border-b border-gray-200">
        <div className="flex items-center justify-between">
          <CardTitle>Misconceptions Detected</CardTitle>
          {summary && summary.total_misconceptions > 0 && (
            <Badge variant="danger" className="text-sm">
              {summary.total_misconceptions} issue{summary.total_misconceptions > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Common misunderstandings affecting multiple students
        </p>

        {summary && summary.overall_roi >= 1 && (
          <div className="mt-3 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">
                  High ROI Opportunity
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Addressing these misconceptions saves {summary.estimated_time_saved_minutes} minutes
                  vs {summary.estimated_time_investment_minutes} minutes invested
                </p>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold ${getROIColor(summary.overall_roi)}`}>
                  {summary.overall_roi.toFixed(1)}x
                </p>
                <p className="text-xs text-gray-600">ROI</p>
              </div>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="p-0">
        {misconceptions.length === 0 ? (
          <div className="px-6 py-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-600 text-lg font-medium">No misconceptions detected</p>
            <p className="text-gray-500 text-sm mt-2">
              Students are understanding concepts correctly!
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {misconceptions.map((misconception) => (
              <div
                key={misconception.id}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1.5">
                    {getPriorityIndicator(misconception.priority)}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className={getPriorityColor(misconception.priority)}>
                        {misconception.priority.toUpperCase()} PRIORITY
                      </Badge>
                      <Badge variant="default">
                        {misconception.students_affected} student{misconception.students_affected > 1 ? 's' : ''} affected
                      </Badge>
                    </div>

                    {/* Misconception details */}
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-1">Students incorrectly believe:</p>
                      <p className="text-gray-900 font-medium text-base mb-3">
                        "{misconception.misconception_text}"
                      </p>

                      <p className="text-sm text-gray-600 mb-1">Correct understanding:</p>
                      <p className="text-green-700 font-medium text-base">
                        {misconception.correct_understanding}
                      </p>
                    </div>

                    {/* Topic */}
                    <div className="mb-4">
                      <span className="inline-flex items-center px-2 py-1 bg-gray-100 rounded text-sm text-gray-700">
                        {misconception.topic}
                      </span>
                    </div>

                    {/* ROI Card */}
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-900 mb-1">
                            Recommended Action
                          </p>
                          <p className="text-sm text-gray-700">
                            {misconception.recommendation.action === 'reteach_whole_class'
                              ? 'Re-teach to whole class'
                              : 'Targeted intervention'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-3xl font-bold ${getROIColor(misconception.recommendation.roi)}`}>
                            {misconception.recommendation.roi.toFixed(1)}x
                          </p>
                          <p className="text-xs text-gray-600">ROI</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-green-200">
                        <div>
                          <p className="text-xs text-gray-600">Time to re-teach</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {misconception.recommendation.time_investment_minutes} minutes
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Time saved vs 1-on-1s</p>
                          <p className="text-sm font-semibold text-green-600">
                            {misconception.recommendation.time_saved_minutes} minutes
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Lesson plan preview */}
                    {misconception.recommendation.lesson_plan && (
                      <div className="mb-4">
                        <button
                          onClick={() => setExpandedId(expandedId === misconception.id ? null : misconception.id)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          {expandedId === misconception.id ? '▼' : '▶'}
                          View Suggested Lesson Plan
                        </button>

                        {expandedId === misconception.id && (
                          <div className="mt-3 p-4 bg-white border border-gray-200 rounded-lg space-y-3">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {misconception.recommendation.lesson_plan.title}
                              </p>
                              <p className="text-xs text-gray-600">
                                Duration: {misconception.recommendation.lesson_plan.duration_minutes} minutes
                              </p>
                            </div>

                            <div>
                              <p className="text-xs font-medium text-gray-700 mb-1">Objective:</p>
                              <p className="text-sm text-gray-600">
                                {misconception.recommendation.lesson_plan.objective}
                              </p>
                            </div>

                            <div>
                              <p className="text-xs font-medium text-gray-700 mb-1">Key Points:</p>
                              <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                                {misconception.recommendation.lesson_plan.key_points.map((point, idx) => (
                                  <li key={idx}>{point}</li>
                                ))}
                              </ul>
                            </div>

                            <div>
                              <p className="text-xs font-medium text-gray-700 mb-1">Success Criteria:</p>
                              <p className="text-sm text-gray-600">
                                {misconception.recommendation.lesson_plan.success_criteria}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Evidence excerpts */}
                    {misconception.evidence_excerpts && misconception.evidence_excerpts.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-medium text-gray-700 mb-2">
                          Evidence from student conversations:
                        </p>
                        <div className="space-y-2">
                          {misconception.evidence_excerpts.slice(0, 2).map((excerpt, idx) => (
                            <div key={idx} className="bg-gray-50 border-l-4 border-gray-300 p-2 rounded">
                              <p className="text-sm text-gray-700 italic">"{excerpt}"</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleMarkAsAddressed(misconception.id)}
                        disabled={addressingId === misconception.id}
                      >
                        {addressingId === misconception.id ? 'Marking...' : 'Mark as Addressed'}
                      </Button>
                      <Button variant="ghost" size="sm">
                        View All {misconception.students_affected} Students
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MisconceptionAlert;
